import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { BurgerMenu } from "./BurgerMenu";
import { HeaderFyLogo } from "./HeaderFyLogo";
import { HeaderLogin } from "./HeaderLogin";

interface Props {
  className?: string;
  backgroundColor?: string;
  menuTextColor?: string;
  menuLink1?: string;
  menuLink1Url?: string;
  menuLink2?: string;
  menuLink2Url?: string;
  menuLink3?: string;
  menuLink3Url?: string;
  menuLink4?: string;
  menuLink4Url?: string;
  menuLink5?: string;
  menuLink5Url?: string;
  menuLink6?: string;
  menuLink6Url?: string;
  logoUrl?: string;
  logoEnableHover?: boolean;
  loginEnableHover?: boolean;
}

export const Header = ({
  className = "",
  backgroundColor = "bg-[var(--fy-blue)]",
  menuTextColor = "text-[var(--fy-grey)]",
  menuLink1 = "ABOUT",
  menuLink1Url = "#about",
  menuLink2 = "EXAMPLES",
  menuLink2Url = "#examples",
  menuLink3 = "PACKAGES",
  menuLink3Url = "#packages",
  menuLink4 = "GALLERY",
  menuLink4Url = "#gallery",
  menuLink5 = "COVERAGE",
  menuLink5Url = "#coverage",
  menuLink6 = "CONTACT",
  menuLink6Url = "#contact",
  logoUrl = "/",
  logoEnableHover = true,
  loginEnableHover = true
}: Props): JSX.Element => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState<'none' | 'blue' | 'content'>('none');
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const [menuOffset, setMenuOffset] = useState<number>(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  
  // Refs for measuring elements
  const logoRef = useRef<HTMLDivElement>(null);
  const loginRef = useRef<HTMLDivElement>(null);

  // Update viewport width when resizing and recalculate menu offset
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      
      // Also recalculate the menu offset when window is resized
      if (window.innerWidth >= 1024 && logoRef.current && loginRef.current) {
        const logoWidth = logoRef.current.offsetWidth;
        const loginWidth = loginRef.current.offsetWidth;
        const difference = loginWidth - logoWidth;
        console.log(`DEBUG - Logo width: ${logoWidth}px, Login width: ${loginWidth}px, Difference: ${difference}px, Applied offset: ${difference/2}px`);
        setMenuOffset(difference / 2);
      }
    };
    
    // Set initial viewport width
    setViewportWidth(window.innerWidth);
    
    // Call once on mount to calculate initial menu offset
    setTimeout(handleResize, 0);
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate dynamic margin based on viewport width (now 10% of viewport)
  const calculateMargin = () => {
    // Use the max content width of 1728px as a constraint
    const MAX_CONTENT_WIDTH = 1728;
    
    // If viewport is wider than max width, calculate based on max width
    if (viewportWidth > MAX_CONTENT_WIDTH) {
      // Center the content area and calculate margin from there
      const sideSpace = (viewportWidth - MAX_CONTENT_WIDTH) / 2;
      return `${sideSpace + (MAX_CONTENT_WIDTH * 0.1)}px`;
    } else {
      // Otherwise use 10% of viewport width
      return `${viewportWidth * 0.1}px`;
    }
  };

  // Get the breakpoint name based on viewport width
  const getBreakpointName = () => {
    if (viewportWidth < 640) {
      return 'Mobile';
    } else if (viewportWidth < 1024) {
      return 'Tablet';
    } else {
      return 'Desktop';
    }
  };

  // Timing constants
  const FADE_DURATION = 200; // Duration of each fade
  const BLUE_HOLD_DURATION = 100; // How long to hold the blue screen

  // Handle opening and closing transitions for the menu
  useEffect(() => {
    if (isMenuOpen) {
      // Start by rendering
      setIsRendered(true);
      setTransitionPhase('blue');
      
      // After blue phase and hold, show content
      const timer = setTimeout(() => {
        setTransitionPhase('content');
        setMenuVisible(true);
      }, FADE_DURATION + BLUE_HOLD_DURATION);
      
      return () => clearTimeout(timer);
    }
  }, [isMenuOpen]);

  const handleMenuClose = () => {
    // First fade out the content
    setMenuVisible(false);
    
    // After content fades, show blue screen
    const contentFadeTimer = setTimeout(() => {
      setTransitionPhase('blue');
      
      // After blue hold, start final fade out
      const blueTimer = setTimeout(() => {
        setTransitionPhase('none');
        
        // Finally remove from DOM after fade completes
        const cleanupTimer = setTimeout(() => {
          setIsRendered(false);
          setIsMenuOpen(false);
        }, FADE_DURATION);
        
        return () => clearTimeout(cleanupTimer);
      }, BLUE_HOLD_DURATION);
      
      return () => clearTimeout(blueTimer);
    }, FADE_DURATION);
    
    return () => clearTimeout(contentFadeTimer);
  };

  // Update scroll timings to match the full transition sequence
  const TOTAL_TRANSITION_TIME = FADE_DURATION * 2 + BLUE_HOLD_DURATION;

  const handleLogoClick = () => {
    handleMenuClose();
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, TOTAL_TRANSITION_TIME);
    
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    }
  };

  const renderLink = (text: string, url: string, isMobile = false) => (
    <a
      href={isHomePage ? url : `/${url}`}
      onClick={(e) => {
        e.preventDefault();
        handleMenuClose();

        if (isHomePage) {
          // If on home page, scroll to section after menu transition
          const element = document.querySelector(url);
          if (element) {
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth' });
            }, TOTAL_TRANSITION_TIME);
          }
        } else {
          // If on another page, navigate to home page + section
          const cleanUrl = url.replace('#', '');
          setTimeout(() => {
            // Store the target section in sessionStorage
            sessionStorage.setItem('scrollTarget', cleanUrl);
            window.location.href = '/';
          }, TOTAL_TRANSITION_TIME);
        }
      }}
      className={`
        relative w-fit whitespace-nowrap uppercase transition-all duration-200
        [font-family:var(--header-menu-font-family)]
        ${isMobile ? 'text-[1.15rem] font-medium transform scale-105' : '[font-size:var(--header-menu-font-size)]'}
        ${!isMobile && '[font-weight:var(--header-menu-font-weight)]'}
        [letter-spacing:var(--header-menu-letter-spacing)]
        [line-height:var(--header-menu-line-height)]
        after:content-[''] after:absolute after:bottom-[-6px] after:left-0 after:w-0 after:h-[2px] after:bg-current 
        after:opacity-80 after:transition-all after:duration-200 lg:hover:after:w-full
        ${menuTextColor}
        cursor-pointer
      `}
      style={isMobile ? { fontSize: '18px', transform: 'scale(1.05)' } : {}}
    >
      {text}
    </a>
  );

  // Add effect to handle scrolling on page load
  useEffect(() => {
    if (isHomePage) {
      const scrollTarget = sessionStorage.getItem('scrollTarget');
      if (scrollTarget) {
        // Clear the stored target
        sessionStorage.removeItem('scrollTarget');
        
        // Wait for page to be ready
        setTimeout(() => {
          const element = document.getElementById(scrollTarget);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100); // Small delay to ensure page is rendered
      }
    }
  }, [isHomePage]);

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] ${backgroundColor} ${className}`}
      style={{ 
        fontSize: 'var(--header-menu-font-size)',
        fontFamily: 'var(--header-menu-font-family)',
        height: '100px',
        width: '100%'
      }}
    >
      {/* Logo positioned at exact left margin */}
      <div 
        ref={logoRef}
        className="absolute flex items-center h-full z-10"
        style={{
          left: calculateMargin(),
          top: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          transform: 'none',
          margin: 0,
          padding: 0
        }}
      >
        <HeaderFyLogo 
          className="relative shrink-0"
          url={logoUrl}
          enableHover={logoEnableHover}
        />
      </div>
      
      {/* Burger menu for mobile and tablet only (below 1024px) */}
      <div 
        className="absolute h-full items-center z-10"
        style={{
          right: calculateMargin(),
          top: 0,
          bottom: 0,
          display: viewportWidth < 1024 ? 'flex' : 'none',
          alignItems: 'center',
          transform: 'none',
          margin: 0,
          padding: 0
        }}
      >
        <BurgerMenu onClick={() => setIsMenuOpen(true)} />
      </div>
      
      {/* Desktop menu links - visually centered between logo and login */}
      <div 
        className="h-full w-full justify-center items-center"
        style={{
          display: viewportWidth >= 1024 ? 'flex' : 'none',
          transform: `translateX(${-menuOffset}px)`
        }}
      >
        <div 
          className="flex justify-between items-center mx-auto"
          style={{
            width: 'clamp(500px, 50%, 800px)'
          }}
        >
          {renderLink(menuLink1, menuLink1Url)}
          {renderLink(menuLink2, menuLink2Url)}
          {renderLink(menuLink3, menuLink3Url)}
          {renderLink(menuLink4, menuLink4Url)}
          {renderLink(menuLink5, menuLink5Url)}
          {renderLink(menuLink6, menuLink6Url)}
        </div>
      </div>
      
      {/* Login button positioned at exact right margin - desktop/TV only (1024px and above) */}
      <div 
        ref={loginRef}
        className="absolute h-full items-center z-10"
        style={{
          right: calculateMargin(),
          top: 0,
          bottom: 0,
          display: viewportWidth >= 1024 ? 'flex' : 'none',
          alignItems: 'center',
          transform: 'none',
          margin: 0,
          padding: 0
        }}
      >
        <HeaderLogin 
          className="relative" 
          enableHover={loginEnableHover}
        />
      </div>
      
      {/* Mobile menu overlay */}
      {isRendered && (
        <div 
          className={`fixed inset-0 z-50 lg:hidden ${backgroundColor}`}
          style={{
            opacity: transitionPhase !== 'none' ? 1 : 0,
            transition: `opacity ${FADE_DURATION}ms ease-in-out`
          }}
        >
          <div 
            className="relative h-full"
            style={{
              opacity: menuVisible ? 1 : 0,
              transition: `opacity ${FADE_DURATION}ms ease-in-out`,
              visibility: menuVisible ? 'visible' : 'hidden'
            }}
          >
            <div className="flex flex-col items-center justify-start pt-[120px] h-full gap-[60px]">
              <div className="mb-14 cursor-pointer" onClick={handleLogoClick}>
                <HeaderFyLogo 
                  className="relative shrink-0 transform scale-[1.39]"
                  url="#"
                  enableHover={logoEnableHover}
                />
              </div>
              {renderLink(menuLink1, menuLink1Url, true)}
              {renderLink(menuLink2, menuLink2Url, true)}
              {renderLink(menuLink3, menuLink3Url, true)}
              {renderLink(menuLink4, menuLink4Url, true)}
              {renderLink(menuLink5, menuLink5Url, true)}
              {renderLink(menuLink6, menuLink6Url, true)}
              <div className="lg:hidden mt-16">
                <HeaderLogin 
                  className="scale-125" 
                  enableHover={loginEnableHover}
                />
              </div>
            </div>
            <button 
              onClick={handleMenuClose}
              className="absolute top-8 text-[var(--fy-grey)]"
              style={{
                right: calculateMargin(),
                transform: 'none',
                margin: 0,
                padding: 0
              }}
              aria-label="Close menu"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 